import { useAccountStore } from '@/stores'
import axios from 'axios'

let isLoggingOut = false

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 12000,
})
export type ApiClientResponse<T = Record<string, unknown>> = {
  code: number
  messages: string[]
  data: T
}

instance.interceptors.request.use((config) => {
  // 防止缓存，给get请求加上时间戳
  if (config.method === 'get') {
    config.params = { ...config.params, t: new Date().getTime() }
  }
  const accountStore = useAccountStore()
  const token = accountStore.account.accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const AUTH_FREE_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/google/complete',
  '/api/v1/auth/refresh',
]

const isAuthFreeEndpoint = (url?: string): boolean =>
  !!url && AUTH_FREE_ENDPOINTS.some((endpoint) => url.includes(endpoint))

// Single-flight refresh: parallel 401s share one /auth/refresh call.
let refreshInFlight: Promise<boolean> | null = null

const performRefresh = async (): Promise<boolean> => {
  const accountStore = useAccountStore()
  const refreshToken = accountStore.account.refreshToken
  if (!refreshToken) {
    return false
  }
  try {
    const { data, code } = (await instance.post('/api/v1/auth/refresh', {
      refresh_token: refreshToken,
    })) as ApiClientResponse
    if (code !== 2000 || !data?.access_token) {
      return false
    }
    // rotation: the refresh token is single-use — persist the new pair
    accountStore.updateSession(data.access_token as string, data.refresh_token as string)
    return true
  } catch {
    return false
  }
}

/** Proactively rotate the session (fresh access token + successor lease).
 * Exported for callers that must hand off a guaranteed-fresh token without
 * waiting for a 401 to trigger the reactive path. */
export const refreshSession = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

const forceLogout = (): void => {
  const accountStore = useAccountStore()
  if (accountStore.account.accessToken && !isLoggingOut) {
    isLoggingOut = true
    // dead lease — involuntary: the login page must say why
    accountStore.logout({ sessionExpired: true })
    setTimeout(() => {
      isLoggingOut = false
    }, 100)
  }
}

instance.interceptors.response.use(
  (axiosResponse) => {
    return { status: axiosResponse.status, ...axiosResponse.data }
  },
  async (error) => {
    const status = error.response?.status
    const data = error.response?.data ?? {}
    const config = error.config ?? {}

    // 401 on a session endpoint: try one transparent refresh + replay
    if (status === 401 && !isAuthFreeEndpoint(config.url) && !config._retried) {
      const refreshed = await refreshSession()
      if (refreshed) {
        config._retried = true
        config.headers = {
          ...(config.headers ?? {}),
          Authorization: `Bearer ${useAccountStore().account.accessToken}`,
        }
        return instance.request(config)
      }
      forceLogout()
      return Promise.reject({ status, ...data })
    }

    // Clear stale auth on 401 Unauthorized (skip auth endpoints — 401 there means bad credentials)
    if (status === 401 && !isAuthFreeEndpoint(config.url) && !isLoggingOut) {
      forceLogout()
    }

    return Promise.reject({ status, ...data })
  },
)

export const apiClient = instance
